import { useCallback } from 'react';
import { Navigate } from 'react-router-dom';

import { ScreenLoader } from '@ocx/ui';
import { useGetNavigationActions } from '@ocx-app/hooks/useGetNavigationActions';
import { useAppRatePendingState } from '@ocx-app/modules/app-rate/useAppRatePendingState';
import { useConfiguration } from '@ocx/data-configuration';
import { useSurveys } from '@ocx-app/modules/survey/survey.context.hook';
import { AppRoutePath } from '@ocx-app/pages/AppRoutePath';

import { ReceiptLayout } from './receipt.layout';
import { Receipt } from './receipt.component';
import { useReceiptController } from './hooks/use-receipt-controller.hook';

type ReceiptControllerProps = {
  modal?: boolean;
  gratitudeLabelHidden?: boolean;
  safeAreaBottomOffsetEnabled?: boolean;
};

export const ReceiptController = ({
  modal,
  gratitudeLabelHidden,
  safeAreaBottomOffsetEnabled,
}: ReceiptControllerProps) => {
  const { pushToHome } = useGetNavigationActions();
  const { loading, receipt, options } = useReceiptController();
  const [, setAppRateIsPending] = useAppRatePendingState();
  const {
    config: { surveys },
  } = useConfiguration();
  const { fetchAndOpenSurveyModal } = useSurveys();
  const handleClose = useCallback(() => {
    pushToHome();
    if (surveys.enabled) {
      fetchAndOpenSurveyModal();
    }
    setAppRateIsPending(true);
  }, [fetchAndOpenSurveyModal, pushToHome, setAppRateIsPending, surveys.enabled]);

  if (!receipt && loading) {
    return (
      <ReceiptLayout modal={modal}>
        <ScreenLoader />
      </ReceiptLayout>
    );
  }

  if (!receipt) {
    return <Navigate to={AppRoutePath.Home} replace />;
  }

  return (
    <ReceiptLayout modal={modal} safeAreaBottomOffsetEnabled={safeAreaBottomOffsetEnabled}>
      <Receipt
        gratitudeLabelHidden={gratitudeLabelHidden}
        addressLine={receipt.address}
        carWashCode={receipt.carWash ? receipt.carWash.code : null}
        receiptLines={receipt.receiptLines}
        total={receipt.total}
        options={options}
        onCloseButtonClick={modal ? undefined : handleClose}
      />
    </ReceiptLayout>
  );
};

export default ReceiptController;
